import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { CommunicationType, ISMSCommunication } from 'shared';

import { LoadingOverlay } from '../../Molecules/LoadingOverlay';
import { useCommunications } from '../../../hooks/useCommunications';

import { SmsInboxMessageDrawer } from './SmsInboxMessageDrawer';
import { SmsInboxTable } from './SmsInboxTable';

function normalizePhone(value?: string) {
  if (!value) {
    return ``;
  }

  return value.replace(/[^\d]/g, ``);
}

export function SmsInboxRoute() {
  const { organisationId } = useParams<{ organisationId?: string }>();
  const { data, isLoading, isFetching, refetch } = useCommunications({ inboxType: `sms` });
  const communications = data?.communications || [];
  const [selectedSmsId, setSelectedSmsId] = useState<number | null>(null);
  const allSmsRows = communications
    .filter(comm => comm.type === CommunicationType.SMS && !!comm.sms)
    .map(comm => comm.sms)
    .filter((sms): sms is NonNullable<typeof sms> => !!sms);
  const inboundRows = allSmsRows
    .filter(sms => sms.direction === `inbound`)
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

      return bTime - aTime;
    });
  const outboundRows = useMemo(() => {
    return allSmsRows.filter(sms => sms.direction === `outbound`);
  }, [allSmsRows]);

  const findLikelyOutbound = (selectedSms: ISMSCommunication) => {
    if (!selectedSms) {
      return null;
    }

    if (selectedSms.matchedActionId) {
      const matchedByAction = outboundRows.find(sms => sms.actionId === selectedSms.matchedActionId);
      if (matchedByAction) {
        return matchedByAction;
      }
    }

    const selectedCreatedAt = selectedSms.createdAt ? new Date(selectedSms.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
    const outboundForContact = outboundRows
      .filter(sms => {
        const smsCreatedAt = sms.createdAt ? new Date(sms.createdAt).getTime() : 0;
        if (smsCreatedAt > selectedCreatedAt) {
          return false;
        }

        if (selectedSms.contactId && sms.contactId) {
          return sms.contactId === selectedSms.contactId;
        }

        const selectedFrom = normalizePhone(selectedSms.from);
        const outboundTo = normalizePhone(sms.to);

        return !!selectedFrom && selectedFrom === outboundTo;
      })
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return bTime - aTime;
      });

    return outboundForContact[0] || null;
  };

  const findManualOutboundAfter = (selectedSms: ISMSCommunication) => {
    if (!selectedSms) {
      return null;
    }

    const selectedCreatedAt = selectedSms.createdAt ? new Date(selectedSms.createdAt).getTime() : 0;

    const candidates = outboundRows
      .filter(sms => {
        if (!sms.isManual) {
          return false;
        }

        const smsCreatedAt = sms.createdAt ? new Date(sms.createdAt).getTime() : 0;
        if (smsCreatedAt <= selectedCreatedAt) {
          return false;
        }

        if (selectedSms.contactId && sms.contactId) {
          return sms.contactId === selectedSms.contactId;
        }

        const selectedFrom = normalizePhone(selectedSms.from);
        const outboundTo = normalizePhone(sms.to);

        return !!selectedFrom && selectedFrom === outboundTo;
      })
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return aTime - bTime;
      });

    return candidates[0] || null;
  };

  const rows = useMemo(() => {
    return inboundRows.map(inbound => {
      if (inbound.contactId && inbound.contactName) {
        return inbound;
      }

      const likelyOutbound = findLikelyOutbound(inbound);
      if (!likelyOutbound) {
        return inbound;
      }

      return {
        ...inbound,
        contactId: inbound.contactId || likelyOutbound.contactId,
        contactName: inbound.contactName || likelyOutbound.contactName,
      };
    });
  }, [inboundRows, outboundRows]);

  const selectedSms = rows.find(sms => sms.id === selectedSmsId) || null;
  const likelyOriginalOutbound = selectedSms ? findLikelyOutbound(selectedSms) : null;
  const manualOutboundAfter = selectedSms ? findManualOutboundAfter(selectedSms) : null;

  return (
    <>
      <LoadingOverlay loading={ isLoading || isFetching }>
        <SmsInboxTable
          rows={ rows }
          onRowClick={ sms => setSelectedSmsId(sms.id) }
        />
      </LoadingOverlay>
      <SmsInboxMessageDrawer
        sms={ selectedSms }
        likelyOriginalOutbound={ likelyOriginalOutbound }
        manualOutboundAfter={ manualOutboundAfter }
        organisationId={ organisationId }
        open={ !!selectedSms }
        onSent={ refetch }
        onClose={ () => setSelectedSmsId(null) }
      />
    </>
  );
}
